import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import api from '../api';
import {
  formatPlanExpiryDate,
  formatPlanPrice,
  formatUkDateTime,
  statusChipColor,
  statusLabel,
} from '../utils/mobileSubscriptionFormat';
import { toast } from 'react-toastify';
import BillingFilterBar, { matchesSearch, normalisePaymentStatus } from './BillingFilterBar';
import BillingPagination, { useBillingPagination } from './BillingPagination';

const childIdOf = (child) =>
  Number(child?.student_user_id || child?.user_id || child?.mobile_user_id || 0);

/** Apple/Google subscriptions are managed in the store, not in the Stripe portal. */
const isExternallyBilled = (child) =>
  (child?.history || []).some((row) => row.manages_billing_externally === true);

/**
 * Mobile app subscriptions on the parent Billing page.
 *
 * Course billing lives in `subscriptions` (Cashier) and is keyed off CoursePrice, so a parent
 * whose only purchase is an app subscription used to see an empty Billing page — the rows are in
 * `mobile_subscriptions` instead. This reads the same endpoints as Setting → Manage Plan.
 */
const MobileSubscriptionList = () => {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [portalBusyId, setPortalBusyId] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const {
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    paginate,
  } = useBillingPagination(`${search}|${statusFilter}`);

  // Search matches the child or the plan; the status filter keeps only matching history rows
  // (payments and subscription periods alike), and drops a child left with none.
  const visibleChildren = useMemo(() => {
    return children
      .map((child) => {
        const history = Array.isArray(child.history) ? child.history : [];
        const childMatches = matchesSearch(search, [child.child_name]);
        const filtered = history.filter(
          (item) =>
            (!statusFilter || normalisePaymentStatus(item.status) === statusFilter) &&
            (childMatches || matchesSearch(search, [item.plan?.name, item.payment_platform]))
        );
        return { ...child, history: filtered, _matches: childMatches || filtered.length > 0 };
      })
      .filter((child) => child._matches && (!statusFilter || child.history.length > 0));
  }, [children, search, statusFilter]);

  // Flatten history so paging is by payment/plan row, same as the other billing tabs,
  // then regroup the current page by child so the header still sits above that child's rows.
  const historyItems = useMemo(() => {
    const items = [];
    visibleChildren.forEach((child) => {
      const history = child.history || [];
      if (!history.length) {
        items.push({ child, item: null });
        return;
      }
      history.forEach((item) => {
        items.push({ child, item });
      });
    });
    return items;
  }, [visibleChildren]);
  const { page, count: rowCount, rows: pagedItems } = paginate(historyItems);
  const pagedChildren = useMemo(() => {
    const groups = [];
    pagedItems.forEach(({ child, item }) => {
      const last = groups[groups.length - 1];
      const childKey = childIdOf(child) || child.child_name;
      if (last && last.childKey === childKey) {
        if (item) last.history.push(item);
        return;
      }
      groups.push({
        childKey,
        child,
        history: item ? [item] : [],
      });
    });
    return groups;
  }, [pagedItems]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('parent/mobile-subscriptions/history');
      const rows = Array.isArray(res.data?.data?.children) ? res.data.data.children : [];
      setChildren(rows);
    } catch (err) {
      setChildren([]);
      setError(err.response?.data?.message || 'Failed to load app subscription history.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const openBillingPortal = async (child) => {
    const id = childIdOf(child);
    if (!id) return;
    setPortalBusyId(id);
    try {
      const res = await api.post('parent/mobile-subscriptions/billing-portal', {
        student_user_id: id,
      });
      const url = String(res.data?.data?.portal_url || '').trim();
      if (!url || !/^https?:\/\//i.test(url)) {
        toast.error('Billing portal URL was not returned.');
        return;
      }
      window.location.assign(url);
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          'Billing portal is not available yet. Subscribe first to manage payments and invoices.'
      );
    } finally {
      setPortalBusyId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  }

  const hasAnyRow = children.some((child) => (child.history || []).length > 0);

  if (!children.length || !hasAnyRow) {
    return (
      <Box
        sx={{
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 2,
          py: 8,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: 'grey.200',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <PhoneIphoneIcon sx={{ color: 'grey.600' }} />
        </Box>
        <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
          No app subscriptions found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Mobile app plans for your children appear here once a subscription is started.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ mt: 1 }}>
      <BillingFilterBar
        search={search}
        onSearchChange={setSearch}
        status={statusFilter}
        onStatusChange={setStatusFilter}
        placeholder="Search by child or plan..."
        statusOptions={[
          { value: 'paid', label: 'Paid' },
          { value: 'failed', label: 'Failed' },
          { value: 'trial', label: 'Trial' },
          { value: 'canceled', label: 'Cancelled' },
          { value: 'expired', label: 'Expired' },
        ]}
      />
      {visibleChildren.length === 0 && (
        <Typography variant="body1" fontWeight={600} sx={{ textAlign: 'center', py: 3 }}>
          No app subscriptions match your search or status filter.
        </Typography>
      )}
      {pagedChildren.map(({ childKey, child, history }) => {
        const current = (child.history || []).find((item) =>
          ['active', 'trial'].includes(String(item.status || '').toLowerCase())
        );
        const id = childIdOf(child);
        const busyPortal = portalBusyId === id;

        return (
          <Box key={childKey}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                mb: 1,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                {child.child_name || 'Child'}
              </Typography>
              {isExternallyBilled(child) ? (
                <Typography variant="caption" color="text.secondary">
                  Managed in the App Store / Google Play
                </Typography>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busyPortal}
                  onClick={() => void openBillingPortal(child)}
                >
                  {busyPortal ? <CircularProgress size={16} /> : 'Stripe invoices'}
                </Button>
              )}
            </Box>

            {current?.current_period_end ? (
              <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                Current plan expires {formatPlanExpiryDate(current.current_period_end)}
              </Alert>
            ) : null}

            {!history.length ? (
              <Typography variant="body2" color="text.secondary">
                No payment history found.
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {history.map((item) => {
                  const isPayment = item.row_type === 'payment';
                  return (
                    <Box
                      key={`${item.row_type || 'subscription'}-${item.id}`}
                      sx={{
                        p: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 1,
                          mb: 0.75,
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2">
                            {item.plan?.name || 'Unknown Plan'}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Status:
                            </Typography>
                            <Chip
                              size="small"
                              label={statusLabel(item.status)}
                              color={statusChipColor(item.status)}
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                        <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                          {isPayment
                            ? formatPlanPrice({
                                price_pence: item.amount_pence,
                                currency: item.currency,
                              })
                            : formatPlanPrice(item.plan)}
                          {!isPayment ? (
                            <Typography component="span" variant="caption" color="text.secondary">
                              /{item.plan?.billing_interval || 'month'}
                            </Typography>
                          ) : null}
                        </Typography>
                      </Box>
                      {isPayment ? (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            Date: {formatUkDateTime(item.paid_at || item.created_at)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Platform: {item.payment_platform || item.payment_method || 'Unknown'}
                            {item.is_automatic ? ' · Automatic charge' : ''}
                          </Typography>
                          {item.receipt_reference ? (
                            <Typography variant="body2" color="text.secondary">
                              Receipt: {item.receipt_reference}
                            </Typography>
                          ) : null}
                        </>
                      ) : (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            Start: {formatUkDateTime(item.current_period_start)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Expires: {formatPlanExpiryDate(item.current_period_end)}
                          </Typography>
                        </>
                      )}
                      {item.auto_charge_due_at ? (
                        <Typography variant="body2" color="text.secondary">
                          Auto charge due: {formatUkDateTime(item.auto_charge_due_at)}
                        </Typography>
                      ) : null}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        );
      })}
      {rowCount > 0 && (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <BillingPagination
            count={rowCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      )}
    </Stack>
  );
};

export default MobileSubscriptionList;
